import React, { useState, useEffect } from 'react';
import { DollarSign, Download, FileText, CheckCircle, Settings, Trash2, Plus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import api from '../../services/api';
import PayrollAdjustmentModal from './PayrollAdjustmentModal';
import { useDialogs } from '../../components/Dialogs';

const PayrollManagement = () => {
    const { ask, dialogs } = useDialogs();
    const [notice, setNotice] = useState(null); // { text, warnings[] } after a month is re-opened
    const today = new Date();
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [year, setYear] = useState(today.getFullYear());
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [error, setError] = useState(null);
    const [approving, setApproving] = useState(false);
    const [adjustingRow, setAdjustingRow] = useState(null);
    const [rowBusy, setRowBusy] = useState(null);

    // A notice about a re-opened month belongs to that month: it goes when another month or year is chosen.
    useEffect(() => { setNotice(null); }, [month, year]);

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    // HR and Admin can re-open an approved month (with a reason); approval itself is open to Finance too.
    const isPlatformAdmin = ['ADMIN', 'HR'].includes(currentUser.role);

    // Deductions Modal
    const [showDeductions, setShowDeductions] = useState(false);

    const generateReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/payroll/report', { params: { month, year } });
            // The endpoint returns { report, warnings }; older builds returned a
            // bare array, so both shapes are accepted.
            const rows = Array.isArray(res.data) ? res.data : (res.data?.report || []);
            setReport(rows);
            setWarnings(Array.isArray(res.data?.warnings) ? res.data.warnings : []);
        } catch (e) {
            setError(e.response?.data?.error || 'Failed to generate report');
            setReport([]);
            setWarnings([]);
        } finally {
            setLoading(false);
        }
    };

    const pendingCount = report.filter(r => r.status !== 'APPROVED').length;

    const approvePayroll = async () => {
        if (!window.confirm(`Approve payroll for all ${pendingCount} pending employee(s) this month? Approved salaries are locked, become visible to employees, and each employee is emailed.`)) return;
        setApproving(true);
        setError(null);
        try {
            // No userIds = everyone still pending; employees approved
            // individually earlier are left as they are.
            const res = await api.post('/payroll/approve', { month, year });
            alert(`Payroll approved for ${res.data?.employeeCount ?? pendingCount} employees.`);
            generateReport(); // Refresh
        } catch (e) {
            // A locked period is an expected outcome, not a crash — surface it
            // inline rather than as a bare alert.
            if (e.response?.data?.code === 'PAYROLL_PERIOD_LOCKED') {
                setError(e.response.data.error);
            } else {
                alert(e.response?.data?.error || 'Failed to approve');
            }
        } finally {
            setApproving(false);
        }
    };

    const approveOne = async (r) => {
        if (!window.confirm(`Approve ${r.user.name}'s salary for this month (net ${formatCurrency(r.netPay)})? It will be locked and emailed to them.`)) return;
        setRowBusy(r.user.id);
        setError(null);
        try {
            await api.post('/payroll/approve', { month, year, userIds: [r.user.id] });
            generateReport();
        } catch (e) {
            setError(e.response?.data?.error || `Failed to approve ${r.user.name}`);
        } finally {
            setRowBusy(null);
        }
    };

    const replaceRow = (updated) => setReport(rows => rows.map(r => (r.user.id === updated.user.id ? updated : r)));

    const reopenPayroll = async () => {
        const reason = await ask({
            title: `Re-open payroll for ${month}/${year}?`,
            message: 'This unlocks the approved payslips of this month so they can be recalculated at current salaries and rules, and approved again. The reason is recorded in the system log. To add LWP that was approved late without changing this month, leave it closed: it is carried into the next payroll.',
            label: 'Reason', required: true, confirmText: 'Re-open',
        });
        if (reason === null) return;
        setApproving(true);
        setError(null);
        setNotice(null);
        try {
            const res = await api.post('/payroll/reopen', { month, year, reason });
            const changes = res.data?.salaryChanges || [];
            setNotice({
                text: `${res.data?.recordCount ?? 0} payroll record(s) re-opened. Review the figures, then approve again.`,
                warnings: changes.map(c => `${c.name}: approved at ${formatCurrency(c.approvedGross)}, salary is now ${formatCurrency(c.currentGross)}. This month will be recalculated at ${formatCurrency(c.currentGross)}; correct it with an adjustment if the new salary should not apply to it.`),
            });
            generateReport();
        } catch (e) {
            setError(e.response?.data?.error || 'Failed to re-open payroll');
        } finally {
            setApproving(false);
        }
    };

    const downloadCsv = () => {
        api.get('/payroll/download-csv', { params: { month, year }, responseType: 'blob' })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `payroll_${year}_${month}.csv`);
                document.body.appendChild(link);
                link.click();
            })
            .catch(() => alert('Failed to download CSV'));
    };

    const viewSlip = (userId) => {
        api.get('/payroll/slip', { params: { userId, month, year }, responseType: 'text' })
            .then(res => {
                const win = window.open('', '_blank');
                win.document.write(res.data);
                win.document.close();
            })
            .catch(e => alert(e.response?.data?.error || 'Failed to get slip'));
    };

    // Rule Management Logic
    const [earningRules, setEarningRules] = useState([]);
    const [deductionRules, setDeductionRules] = useState([]);
    const [newEarningRule, setNewEarningRule] = useState({ name: '', type: 'PERCENTAGE', value: '' });
    const [newDeductionRule, setNewDeductionRule] = useState({ name: '', type: 'FIXED', value: '' });

    const fetchRules = async () => {
        try {
            const [deductions, earnings] = await Promise.all([
                api.get('/payroll/deductions'),
                api.get('/payroll/earnings')
            ]);
            setDeductionRules(deductions.data);
            setEarningRules(earnings.data);
        } catch (e) {
            console.error(e);
        }
    };

    const handleSaveRule = async (type) => {
        const isEarning = type === 'EARNING';
        const rule = isEarning ? newEarningRule : newDeductionRule;
        const setRule = isEarning ? setNewEarningRule : setNewDeductionRule;
        const endpoint = isEarning ? '/payroll/earnings' : '/payroll/deductions';
        const defaultState = isEarning ? { name: '', type: 'PERCENTAGE', value: '' } : { name: '', type: 'FIXED', value: '' };

        if (!rule.name || !rule.value) return;
        try {
            if (rule.id) {
                await api.put(`${endpoint}/${rule.id}`, rule);
            } else {
                await api.post(endpoint, rule);
            }
            setRule(defaultState);
            fetchRules();
        } catch (e) {
            // The endpoint now returns specific messages (invalid type, value out of
            // range, duplicate name) — showing them beats a generic "Failed".
            alert(e.response?.data?.error || 'Failed to save rule');
        }
    };

    const deleteRule = async (id, type) => {
        if (!confirm('Delete this rule?')) return;
        const endpoint = type === 'EARNING' ? '/payroll/earnings' : '/payroll/deductions';
        try {
            await api.delete(`${endpoint}/${id}`);
            fetchRules();
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to delete rule');
        }
    };

    const [baseCurrency, setBaseCurrency] = useState('INR');

    const fetchBaseConfig = async () => {
        try {
            const res = await api.get('/admin/config');
            setBaseCurrency(res.data.baseCurrency || 'INR');
        } catch (e) {
            console.error('Failed to fetch config', e);
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: baseCurrency }).format(val);

    useEffect(() => {
        generateReport();
        fetchBaseConfig();
    }, []);

    useEffect(() => {
        if (showDeductions) fetchRules();
    }, [showDeductions]);

    // Validation for Earnings
    const totalEarningPercent = earningRules
        .filter(r => r.type === 'PERCENTAGE')
        .reduce((sum, r) => sum + Number(r.value), 0);

    const isEarningTotalValid = Math.abs(totalEarningPercent - 100) < 0.1;

    const isAllApproved = report.length > 0 && report.every(r => r.status === 'APPROVED');
    const hasData = report.length > 0;

    return (
        <div className="container section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <PageHeader crumbs={crumbsFor('runPayroll')} />
                <button
                    onClick={() => setShowDeductions(!showDeductions)}
                    style={{ display: 'flex', alignItems: 'center', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                >
                    <Settings size={18} style={{ marginRight: '0.5rem' }} />
                    {showDeductions ? 'Hide Configuration' : 'Configure Rules'}
                </button>
            </div>

            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <DollarSign style={{ marginRight: '0.75rem', color: '#2563eb' }} /> Payroll Management
            </h1>

            {showDeductions && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '2rem' }}>
                    {/* Earnings Configuration */}
                    <div className="glass-card" style={{ borderLeft: '4px solid #10b981' }}>
                        <h3>Earnings Rules</h3>
                        {!isEarningTotalValid && (
                            <div style={{ color: 'orange', fontSize: '0.8rem', marginBottom: '10px' }}>
                                Warning: Total Percentage is {totalEarningPercent.toFixed(2)}% (Should be ~100%)
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <input type="text" placeholder="Name" value={newEarningRule.name} onChange={e => setNewEarningRule({ ...newEarningRule, name: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid var(--border)', flex: 1 }} />
                            <select value={newEarningRule.type} onChange={e => setNewEarningRule({ ...newEarningRule, type: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid var(--border)', width: '100px' }}>
                                <option value="PERCENTAGE">% of CTC</option>
                                <option value="FIXED">Fixed</option>
                            </select>
                            <input type="number" placeholder="Value" value={newEarningRule.value} onChange={e => setNewEarningRule({ ...newEarningRule, value: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid var(--border)', width: '70px' }} />
                            <button onClick={() => handleSaveRule('EARNING')} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '0.8rem' }}>
                                {newEarningRule.id ? 'Save' : 'Add'}
                            </button>
                            {newEarningRule.id && <button onClick={() => setNewEarningRule({ name: '', type: 'PERCENTAGE', value: '' })} style={{ padding: '5px', border: 'none', background: 'var(--bg-secondary)' }}>X</button>}
                        </div>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {earningRules.map(rule => (
                                <li key={rule.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', borderBottom: '1px solid #eee', alignItems: 'center', fontSize: '0.9rem' }}>
                                    <span style={{ flex: 1 }}><strong>{rule.name}</strong>: {rule.type === 'FIXED' ? `₹${rule.value}` : `${rule.value}%`}</span>
                                    <div>
                                        <button onClick={() => setNewEarningRule(rule)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: '5px' }}>Edit</button>
                                        <button onClick={() => deleteRule(rule.id, 'EARNING')} style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Deductions Configuration */}
                    <div className="glass-card" style={{ borderLeft: '4px solid #ef4444' }}>
                        <h3>Deduction Rules</h3>
                        <div style={{ display: 'flex', gap: '5px', marginBottom: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <input type="text" placeholder="Name" value={newDeductionRule.name} onChange={e => setNewDeductionRule({ ...newDeductionRule, name: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid var(--border)', flex: 1 }} />
                            <select value={newDeductionRule.type} onChange={e => setNewDeductionRule({ ...newDeductionRule, type: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid var(--border)', width: '100px' }}>
                                <option value="FIXED">Fixed</option>
                                <option value="PERCENTAGE">% of Gross</option>
                            </select>
                            <input type="number" placeholder="Value" value={newDeductionRule.value} onChange={e => setNewDeductionRule({ ...newDeductionRule, value: e.target.value })} style={{ padding: '5px', borderRadius: '4px', border: '1px solid var(--border)', width: '70px' }} />
                            <button onClick={() => handleSaveRule('DEDUCTION')} className="btn btn-primary" style={{ padding: '5px 10px', fontSize: '0.8rem' }}>
                                {newDeductionRule.id ? 'Save' : 'Add'}
                            </button>
                            {newDeductionRule.id && <button onClick={() => setNewDeductionRule({ name: '', type: 'FIXED', value: '' })} style={{ padding: '5px', border: 'none', background: 'var(--bg-secondary)' }}>X</button>}
                        </div>
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {deductionRules.map(rule => (
                                <li key={rule.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', borderBottom: '1px solid #eee', alignItems: 'center', fontSize: '0.9rem' }}>
                                    <span style={{ flex: 1 }}><strong>{rule.name}</strong>: {rule.type === 'FIXED' ? `₹${rule.value}` : `${rule.value}%`}</span>
                                    <div>
                                        <button onClick={() => setNewDeductionRule(rule)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', marginRight: '5px' }}>Edit</button>
                                        <button onClick={() => deleteRule(rule.id, 'DEDUCTION')} style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={14} /></button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="glass-card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Month</label>
                            <select
                                value={month}
                                onChange={e => setMonth(e.target.value)}
                                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--border)', minWidth: '150px' }}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Year</label>
                            <input
                                type="number"
                                value={year}
                                onChange={e => setYear(e.target.value)}
                                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--border)', width: '100px' }}
                            />
                        </div>
                        <button onClick={generateReport} className="btn btn-primary" disabled={loading}>
                            {loading ? 'Generating...' : 'Generate Report'}
                        </button>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {hasData && !isAllApproved && (
                            <button
                                onClick={approvePayroll}
                                className="btn"
                                style={{ backgroundColor: '#f59e0b', color: 'white', border: 'none' }}
                                disabled={approving}
                            >
                                {approving ? 'Working…' : `Approve all pending (${pendingCount})`}
                            </button>
                        )}
                        {hasData && !isAllApproved && pendingCount < report.length && isPlatformAdmin && (
                            <button onClick={reopenPayroll} className="btn btn-secondary" disabled={approving} title="Unlock the employees already approved this month">
                                Re-open approved
                            </button>
                        )}
                        {hasData && isAllApproved && (
                            <>
                                {/* Approved periods are locked, so this is a status
                                    indicator rather than a re-runnable action. */}
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                    padding: '0.5rem 1rem', borderRadius: '0.5rem',
                                    background: 'var(--accent-subtle)', color: 'var(--primary)',
                                    fontSize: '0.875rem', fontWeight: 600,
                                }}>
                                    <CheckCircle size={18} /> Approved &amp; locked
                                </span>
                                {isPlatformAdmin && (
                                    <button onClick={reopenPayroll} className="btn btn-secondary" disabled={approving}>
                                        {approving ? 'Working…' : 'Re-open Period'}
                                    </button>
                                )}
                            </>
                        )}
                        <button onClick={downloadCsv} className="btn btn-secondary" disabled={report.length === 0}>
                            <Download size={18} style={{ marginRight: '0.5rem' }} /> Download CSV
                        </button>
                    </div>
                </div>
                {error && <div style={{ color: 'var(--danger, #dc2626)', marginTop: '1rem', fontSize: '0.9rem' }}>{error}</div>}
                {notice && (
                    <div role="status" data-testid="reopen-notice" style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                        <div style={{ color: '#047857' }}>{notice.text}</div>
                        {notice.warnings.map((w, i) => <div key={i} role="alert" style={{ color: '#b45309', marginTop: '0.25rem' }}>{w}</div>)}
                    </div>
                )}
                {warnings.length > 0 && (
                    <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '0.5rem', padding: '0.75rem' }}>
                        {warnings.slice(0, 5).map((w, i) => <div key={i}>{w}</div>)}
                        {warnings.length > 5 && <div>…and {warnings.length - 5} more.</div>}
                    </div>
                )}
            </div>

            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid #e5e7eb' }}>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>ID</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Employee</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Monthly CTC</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Paid Days</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Earned Gross</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Deductions</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Net Pay</th>
                                <th style={{ padding: '1rem', fontWeight: '600' }}>Status</th>
                                <th style={{ padding: '1rem', fontWeight: '600', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.length === 0 ? (
                                <tr><td colSpan="9" style={{ padding: '2rem', textAlign: 'center' }}>No data available. Click Generate.</td></tr>
                            ) : (
                                report.map((r) => (
                                    <tr key={r.user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>{r.user.employeeId || '-'}</td>
                                        <td style={{ padding: '1rem' }}>
                                            <div style={{ fontWeight: '500' }}>{r.user.name}</div>
                                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{r.user.email}</div>
                                        </td>
                                        <td style={{ padding: '1rem', color: 'var(--text-muted)' }}>
                                            {formatCurrency(r.grossSalary)}
                                            {r.salaryChangedFrom != null && <div role="status" style={{ fontSize: '0.75rem', color: '#b45309' }}>It was {formatCurrency(r.salaryChangedFrom)} when approved</div>}
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            {r.payableDays} / {r.daysInMonth}
                                            {(r.unpaidDaysCount > 0) && <div style={{ fontSize: '0.8rem', color: '#b91c1c' }}>{r.unpaidDaysCount} LWP</div>}
                                        </td>
                                        {/* Earned Gross is the prorated figure the rest of the row
                                            reconciles against: Earned Gross − Deductions = Net Pay.
                                            Monthly CTC alone never balanced once unpaid days existed. */}
                                        <td style={{ padding: '1rem' }}>{formatCurrency(r.earnedGross)}</td>
                                        <td style={{ padding: '1rem', color: '#b91c1c' }}>
                                            -{formatCurrency(r.taxDeductions)}
                                            {(r.carryOver || []).length > 0 && (
                                                <div data-testid={`carry-${r.user.id}`} style={{ fontSize: '0.75rem', color: '#b45309' }}>
                                                    {(r.carryOver || []).map(e => `${e.amount >= 0 ? 'LWP carried' : 'LWP given back'}: ${formatCurrency(Math.abs(e.amount))} (${new Date(0, e.month - 1).toLocaleString('default', { month: 'short' })} ${e.year})`).join(', ')}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '1rem', fontWeight: 'bold' }}>{formatCurrency(r.netPay)}</td>
                                        <td style={{ padding: '1rem' }}>
                                            {(() => {
                                                const s = r.status === 'APPROVED'
                                                    ? { label: 'APPROVED', bg: '#d1fae5', fg: '#065f46' }
                                                    : r.isAdjusted
                                                        ? { label: 'ADJUSTED', bg: '#fef3c7', fg: '#92400e' }
                                                        : { label: r.status || 'DRAFT', bg: '#fee2e2', fg: '#991b1b' };
                                                return (
                                                    <span title={r.isAdjusted ? `${r.adjustments?.length || 0} manual adjustment(s)` : undefined} style={{
                                                        padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem',
                                                        backgroundColor: s.bg, color: s.fg, whiteSpace: 'nowrap',
                                                    }}>
                                                        {s.label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td style={{ padding: '1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {r.status !== 'APPROVED' && (
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginBottom: '0.35rem' }}>
                                                    <button onClick={() => setAdjustingRow(r)} className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}>
                                                        Adjust
                                                    </button>
                                                    <button onClick={() => approveOne(r)} disabled={rowBusy === r.user.id} className="btn" style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', background: '#10b981', color: 'white', border: 'none' }}>
                                                        {rowBusy === r.user.id ? '…' : 'Approve'}
                                                    </button>
                                                </div>
                                            )}
                                            {/* ADMIN/HR/FINANCE may preview an unapproved slip — the
                                                backend has always allowed it, but the button used to be
                                                disabled while still rendering as clickable. */}
                                            <button
                                                onClick={() => viewSlip(r.user.id)}
                                                title={r.status === 'APPROVED' ? 'View payslip' : 'Preview draft payslip'}
                                                style={{
                                                    border: 'none', background: 'none', cursor: 'pointer',
                                                    color: r.status === 'APPROVED' ? 'var(--primary)' : 'var(--text-muted)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                                                    gap: '0.25rem', width: '100%',
                                                }}
                                            >
                                                <FileText size={16} /> {r.status === 'APPROVED' ? 'Slip' : 'Preview'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {adjustingRow && (
                <PayrollAdjustmentModal
                    row={adjustingRow}
                    month={Number(month)}
                    year={Number(year)}
                    formatCurrency={formatCurrency}
                    onClose={() => setAdjustingRow(null)}
                    onRowUpdated={replaceRow}
                />
            )}
            {dialogs}
        </div>
    );
};

export default PayrollManagement;
